import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Settings as SettingsIcon, User, Lock, Mail, Bell, ShieldCheck, Download, Trash2, LogOut, Clock, Building2, Globe, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SUPPORTED_LANGUAGES } from "@/i18n";

const fmtHour = (h) => {
  const ampm = h < 12 ? "AM" : "PM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:00 ${ampm}`;
};
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => h);
const TZ_OPTIONS = (() => {
  try { return Intl.supportedValuesOf("timeZone"); }
  catch { return ["UTC", "America/New_York", "America/Los_Angeles", "Europe/London", "Europe/Paris", "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Australia/Sydney"]; }
})();
const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const Section = ({ icon: Icon, title, desc, children, testid }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-6 md:p-7" data-testid={testid}>
    <div className="flex items-center gap-2.5 mb-1">
      <Icon className="h-5 w-5 text-[#202123]" />
      <h2 className="font-display text-lg font-semibold text-navy">{title}</h2>
    </div>
    {desc && <p className="text-sm text-slatebody mb-5 max-w-2xl">{desc}</p>}
    {children}
  </section>
);

const Row = ({ label, hint, children, testid }) => (
  <div className="flex items-center justify-between gap-4 py-2.5" data-testid={testid}>
    <div className="min-w-0">
      <p className="text-sm text-navy">{label}</p>
      {hint && <p className="text-xs text-slatebody mt-0.5">{hint}</p>}
    </div>
    {children}
  </div>
);

export default function Settings() {
  const { user, setUser, setSession, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const legalPfx = (i18n.language || "en").split("-")[0] === "fr" ? "/fr" : "";
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [canTeam, setCanTeam] = useState(false);
  const [orgInfo, setOrgInfo] = useState(null);
  const [name, setName] = useState(user?.full_name || "");
  const [tz, setTz] = useState(user?.timezone || browserTz);
  const [savingProfile, setSavingProfile] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [converting, setConverting] = useState(false);
  const [orgLang, setOrgLang] = useState("en");

  useEffect(() => {
    if (orgInfo?.default_language) setOrgLang(orgInfo.default_language);
  }, [orgInfo?.default_language]);

  const saveOrgLang = async (code) => {
    setOrgLang(code);
    try {
      await api.put("/org/default-language", { default_language: code });
      toast.success(t("language.orgDefaultUpdated"));
    } catch (err) { toast.error(apiError(err)); }
  };

  useEffect(() => {
    api.get("/notifications", { params: {} })
      .then((r) => { setPrefs(r.data.prefs); setCanTeam(r.data.can_team); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.is_primary_admin === true || user?.is_org_admin === true) {
      api.get("/org/settings").then((r) => setOrgInfo(r.data)).catch(() => {});
    }
  }, [user?.is_org_admin, user?.is_primary_admin]);

  useEffect(() => {
    if (user) { setName(user.full_name || ""); setTz(user.timezone || browserTz); }
  }, [user]);

  const tzOptions = Array.from(new Set([tz, browserTz, ...TZ_OPTIONS])).filter(Boolean);

  const setPref = async (key, val) => {
    const next = { ...prefs, [key]: val };
    setPrefs(next);
    try { await api.put("/notifications/preferences", next); }
    catch (err) { toast.error(apiError(err)); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await api.put("/account/profile", { full_name: name, timezone: tz });
      setUser(res.data.user);
      toast.success(t("settings.toastProfileUpdated"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingProfile(false); }
  };

  const downloadCsv = (items, cols, filenameBase) => {
    const esc = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [cols.join(",")].concat((items || []).map((e) => cols.map((c) => esc(e[c])).join(",")));
    const blob = new Blob(["\ufeff" + rows.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `${filenameBase}-${stamp}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportData = async () => {
    try {
      const res = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `${t("settings.exportFilename")}-${stamp}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("settings.toastDataExported"));
    } catch (err) { toast.error(apiError(err)); }
  };

  const exportLogsCsv = async () => {
    try {
      const res = await api.get("/account/export");
      const logs = res.data?.log_entries || [];
      const cols = ["date", "activity", "hours", "minutes", "linked_objective", "notes", "created_at"];
      downloadCsv(logs, cols, t("settings.exportLogsFilename"));
      toast.success(t("settings.toastLogsExported", { count: logs.length }));
    } catch (err) { toast.error(apiError(err)); }
  };

  const exportReportsCsv = async () => {
    try {
      const res = await api.get("/account/export");
      const reps = res.data?.reports || [];
      const cols = ["title", "style", "length", "range_type", "start_date", "end_date",
                    "shared_with_supervisor", "is_compiled", "created_at", "updated_at", "content"];
      downloadCsv(reps, cols, t("settings.exportReportsFilename"));
      toast.success(t("settings.toastReportsExported", { count: reps.length }));
    } catch (err) { toast.error(apiError(err)); }
  };

  const exportArchive = async () => {
    try {
      const res = await api.get("/account/export/archive", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `${t("settings.exportArchiveFilename")}-${stamp}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("settings.toastArchiveExported"));
    } catch (err) { toast.error(apiError(err)); }
  };

  const logoutAll = async () => {
    try { await api.post("/auth/logout-all"); toast.success(t("settings.toastSignedOutAll")); logout(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const convertToOrg = async () => {
    if (!orgName.trim()) { toast.error(t("settings.toastEnterOrgName")); return; }
    setConverting(true);
    try {
      const res = await api.post("/account/convert-to-organization", { org_name: orgName.trim() });
      setUser(res.data.user);
      toast.success(res.data.message || t("settings.toastNowOrg"));
      setOrgName("");
      navigate("/app/organization");
    } catch (err) { toast.error(apiError(err)); }
    finally { setConverting(false); }
  };

  return (
    <div className="animate-fade-up max-w-3xl">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-navy flex items-center gap-2"><SettingsIcon className="h-7 w-7 text-[#202123]" /> {t("settings.accountSettings")}</h1>
        <p className="text-slatebody mt-1">{t("settings.settingsSubtitle")}</p>
      </div>

      <div className="space-y-6">
        <Section icon={Globe} title={t("language.label")} testid="settings-language">
          <LanguageSwitcher variant="full" />
          {(user?.is_primary_admin === true || user?.is_org_admin === true) && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <p className="font-medium text-navy mb-1.5">{t("language.orgDefault")}</p>
              <p className="text-sm text-slatebody mb-3 max-w-2xl">{t("language.orgDefaultDesc")}</p>
              <Select value={orgLang} onValueChange={saveOrgLang}>
                <SelectTrigger className="max-w-xs" data-testid="settings-org-language-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {SUPPORTED_LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.native}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </Section>

        <Section icon={User} title={t("settings.profile")} testid="settings-profile">
          <div className="space-y-4">
            <div>
              <Label htmlFor="s-name">{t("settings.fullName")}</Label>
              <Input id="s-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="settings-name-input" />
            </div>
            <div>
              <Label>{t("settings.timezone")} <span className="text-slatebody font-normal">{t("settings.timezoneHint")}</span></Label>
              <Select value={tz} onValueChange={setTz}>
                <SelectTrigger className="mt-1.5" data-testid="settings-timezone-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {tzOptions.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" onClick={saveProfile} disabled={savingProfile} data-testid="settings-save-profile-btn">
              {savingProfile ? t("settings.saving") : t("settings.saveProfile")}
            </Button>
          </div>
        </Section>

        {user?.account_type !== "organization" && (
          <Section icon={Building2} title={t("settings.upgradeOrgTitle")} desc={t("settings.upgradeOrgDesc")} testid="settings-convert-org">
            <div className="space-y-4 max-w-md">
              <div>
                <Label htmlFor="s-orgname">{t("settings.orgName")}</Label>
                <Input id="s-orgname" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder={t("settings.orgNamePlaceholder")} className="mt-1.5" data-testid="convert-org-name-input" />
              </div>
              <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" onClick={convertToOrg} disabled={converting || !orgName.trim()} data-testid="convert-org-btn">
                {converting ? t("settings.settingUp") : t("settings.convertBtn")}
              </Button>
            </div>
          </Section>
        )}

        <Section icon={Lock} title={t("settings.security")} desc={t("settings.securityDesc")} testid="settings-security">
          <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
            <div><p className="text-sm text-navy flex items-center gap-1.5"><Mail className="h-4 w-4 text-slatebody" /> {t("settings.email")}</p><p className="text-xs text-slatebody mt-0.5">{user?.email}{user?.pending_email ? ` · ${t("settings.pendingLabel")}: ${user.pending_email}` : ""}</p></div>
            <ChangeEmailDialog />
          </div>
          <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
            <div><p className="text-sm text-navy flex items-center gap-1.5"><Lock className="h-4 w-4 text-slatebody" /> {t("settings.passwordLabel")}</p><p className="text-xs text-slatebody mt-0.5">{t("settings.passwordHint")}</p></div>
            <ChangePasswordDialog onChanged={(token) => setSession(token, user)} />
          </div>
          <Row label={t("settings.signOut")} hint={t("settings.signOutHint")} testid="settings-signout-row">
            <Button variant="outline" onClick={logout} data-testid="settings-signout-btn"><LogOut className="h-4 w-4 mr-1.5" /> {t("settings.signOut")}</Button>
          </Row>
          <Row label={t("settings.signOutAll")} hint={t("settings.signOutAllHint")} testid="settings-signout-all-row">
            <Button variant="outline" onClick={logoutAll} data-testid="settings-signout-all-btn">{t("settings.signOutEverywhere")}</Button>
          </Row>
        </Section>

        <Section icon={Bell} title={t("settings.notifications")} desc={t("settings.notificationsDesc")} testid="settings-notifications">
          {!prefs ? <p className="text-sm text-slatebody">{t("common.loading")}</p> : (
            <div className="divide-y divide-slate-100">
              <Row label={t("settings.weeklyRecap")} hint={t("settings.weeklyRecapHint")} testid="settings-pref-recap">
                <Switch checked={prefs.weekly_recap_email ?? true} onCheckedChange={(v) => setPref("weekly_recap_email", v)} data-testid="settings-pref-weekly-recap" />
              </Row>
              <Row label={t("settings.dailyReminder")} hint={t("settings.dailyReminderHint")} testid="settings-pref-reminder">
                <Switch checked={prefs.daily_reminder ?? true} onCheckedChange={(v) => setPref("daily_reminder", v)} data-testid="settings-pref-daily-reminder" />
              </Row>
              {(prefs.daily_reminder ?? true) && (
                <Row label={t("settings.remindMeAt")} hint={t("settings.remindMeAtHint")} testid="settings-pref-reminder-hour">
                  <Select value={String(prefs.reminder_hour ?? 17)} onValueChange={(v) => setPref("reminder_hour", Number(v))}>
                    <SelectTrigger className="h-9 w-28 text-sm" data-testid="settings-reminder-hour-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{HOUR_OPTIONS.map((h) => <SelectItem key={h} value={String(h)}>{fmtHour(h)}</SelectItem>)}</SelectContent>
                  </Select>
                </Row>
              )}
              <Row label={t("settings.atRiskAlerts")} hint={t("settings.atRiskHint")} testid="settings-pref-atrisk">
                <Switch checked={prefs.at_risk ?? true} onCheckedChange={(v) => setPref("at_risk", v)} data-testid="settings-pref-at-risk" />
              </Row>
              {canTeam && (
                <Row label={t("settings.teamPings")} hint={t("settings.teamPingsHint")} testid="settings-pref-team">
                  <Switch checked={prefs.team_reports ?? true} onCheckedChange={(v) => setPref("team_reports", v)} data-testid="settings-pref-team-reports" />
                </Row>
              )}
            </div>
          )}
        </Section>

        <Section icon={ShieldCheck} title={t("settings.dataPrivacyTitle")} desc={t("settings.dataPrivacyDesc")} testid="settings-data">
          <div className="flex flex-wrap gap-3 mb-6">
            <Button variant="outline" onClick={exportData} data-testid="settings-export-btn"><Download className="h-4 w-4 mr-1.5" /> {t("settings.exportMyData")}</Button>
            <Button variant="outline" onClick={exportLogsCsv} data-testid="settings-export-csv-btn"><Download className="h-4 w-4 mr-1.5" /> {t("settings.exportLogsCsv")}</Button>
            <Button variant="outline" onClick={exportReportsCsv} data-testid="settings-export-reports-csv-btn"><Download className="h-4 w-4 mr-1.5" /> {t("settings.exportReportsCsv")}</Button>
            <Button onClick={exportArchive} data-testid="settings-export-archive-btn" className="bg-[#202123] hover:bg-[#000] text-white"><Download className="h-4 w-4 mr-1.5" /> {t("settings.exportEverything")}</Button>
          </div>
          <p className="text-xs text-slatebody mb-6" data-testid="settings-data-ownership-msg">
            {user?.account_type === "organization" ? t("settings.dataOwnershipOrg") : t("settings.dataOwnershipIndiv")}
          </p>
          <div className="rounded-lg border border-red-200 bg-red-50/50 p-4 space-y-4" data-testid="settings-danger-zone">
            <p className="font-medium text-red-700 text-sm">{t("settings.dangerZone")}</p>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div><p className="text-sm text-navy">{t("settings.deleteMyData")}</p><p className="text-xs text-slatebody">{t("settings.deleteMyDataHint")}</p></div>
              <DestructiveDialog
                trigger={<Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-100" data-testid="settings-delete-data-btn"><Trash2 className="h-4 w-4 mr-1.5" /> {t("settings.deleteMyData")}</Button>}
                title={t("settings.deleteDataDialogTitle")}
                description={t("settings.deleteDataDialogDesc")}
                confirmWord="DELETE"
                testid="delete-data-dialog"
                onConfirm={async () => {
                  await api.post("/account/purge-data");
                  toast.success(t("settings.toastContentDeleted"));
                }}
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-red-200 pt-4">
              <div><p className="text-sm text-navy">{t("settings.deleteMyAccount")}</p><p className="text-xs text-slatebody">{t("settings.deleteMyAccountHint")}</p></div>
              <DestructiveDialog
                trigger={<Button className="bg-red-800 hover:bg-red-900 text-white" data-testid="settings-delete-account-btn"><Trash2 className="h-4 w-4 mr-1.5" /> {t("settings.deleteAccountBtn")}</Button>}
                title={t("settings.deleteAccountDialogTitle")}
                description={t("settings.deleteAccountDialogDesc")}
                confirmWord="DELETE"
                testid="delete-account-dialog"
                note={orgInfo && user && orgInfo.admin_user_id === user.id && orgInfo.member_count > 1
                  ? t("settings.ownerNote", { org: orgInfo.name, count: orgInfo.member_count })
                  : undefined}
                secondaryAction={orgInfo && user && orgInfo.admin_user_id === user.id && orgInfo.member_count > 1
                  ? { label: t("settings.transferInstead"), onClick: () => navigate("/app/organization") }
                  : undefined}
                onConfirm={async () => {
                  await api.delete("/account");
                  localStorage.removeItem("jotbay_token");
                  window.location.href = "/";
                }}
              />
            </div>
          </div>
        </Section>

        <Section icon={FileText} title={t("settings.legalTitle")} desc={t("settings.legalDesc")} testid="settings-legal">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm" data-testid="settings-legal-links">
            <a href={`${legalPfx}/terms`} target="_blank" rel="noopener noreferrer" className="text-[#202123] font-medium hover:underline" data-testid="settings-legal-terms">{t("auth.tosFull")}</a>
            <a href={`${legalPfx}/privacy`} target="_blank" rel="noopener noreferrer" className="text-[#202123] font-medium hover:underline" data-testid="settings-legal-privacy">{t("auth.privacyFull")}</a>
            <a href={`${legalPfx}/ai-use`} target="_blank" rel="noopener noreferrer" className="text-[#202123] font-medium hover:underline" data-testid="settings-legal-aiuse">{t("auth.aiUseFull")}</a>
            <a href={`${legalPfx}/cookies`} target="_blank" rel="noopener noreferrer" className="text-[#202123] font-medium hover:underline" data-testid="settings-legal-cookies">{t("auth.cookiesFull")}</a>
          </div>
        </Section>
      </div>
    </div>
  );
}

function ChangePasswordDialog({ onChanged }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const rules = [
    { label: t("settings.pwRule8"), ok: next.length >= 8 },
    { label: t("settings.pwRuleLetter"), ok: /[a-zA-Z]/.test(next) },
    { label: t("settings.pwRuleNumber"), ok: /[0-9]/.test(next) },
  ];
  const matches = confirm.length > 0 && next === confirm;
  const valid = rules.every((r) => r.ok) && matches && cur.length > 0;
  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/change-password", { current_password: cur, new_password: next });
      if (res.data.token) onChanged?.(res.data.token);
      toast.success(res.data.message || t("settings.toastPasswordChanged"));
      setOpen(false); setCur(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="change-password-open-btn">{t("settings.changePassword")}</Button></DialogTrigger>
      <DialogContent className="bg-white" data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("settings.changePassword")}</DialogTitle>
          <DialogDescription>{t("settings.changePasswordDesc")}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>{t("settings.currentPassword")}</Label><PasswordInput value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1.5" data-testid="current-password-input" /></div>
          <div>
            <Label>{t("settings.newPassword")}</Label>
            <PasswordInput value={next} onChange={(e) => setNext(e.target.value)} className="mt-1.5" data-testid="new-password-input" />
            <ul className="mt-2 space-y-1">
              {rules.map((r, i) => <li key={i} className={`text-xs ${r.ok ? "text-[#202123]" : "text-slatebody"}`}>{r.ok ? "✓" : "○"} {r.label}</li>)}
            </ul>
          </div>
          <div>
            <Label>{t("settings.confirmNewPassword")}</Label>
            <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="confirm-password-input" />
            {confirm.length > 0 && !matches && <p className="text-xs text-red-600 mt-1.5" data-testid="change-password-mismatch">{t("settings.passwordsMismatch")}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" disabled={!valid || busy} onClick={submit} data-testid="change-password-submit-btn">{busy ? t("settings.saving") : t("settings.changePassword")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangeEmailDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/change-email", { current_password: pw, new_email: email });
      toast.success(res.data.message || t("settings.toastCheckNewEmail"));
      setOpen(false); setPw(""); setEmail("");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="change-email-open-btn">{t("settings.changeEmail")}</Button></DialogTrigger>
      <DialogContent className="bg-white" data-testid="change-email-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("settings.changeEmail")}</DialogTitle>
          <DialogDescription>{t("settings.changeEmailDesc")}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>{t("settings.currentPassword")}</Label><PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5" data-testid="change-email-password-input" /></div>
          <div><Label>{t("settings.newEmail")}</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="change-email-new-input" /></div>
        </div>
        <DialogFooter>
          <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" disabled={!pw || !email || busy} onClick={submit} data-testid="change-email-submit-btn">{busy ? t("settings.sending") : t("settings.sendConfirmation")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DestructiveDialog({ trigger, title, description, confirmWord, onConfirm, testid, note, secondaryAction }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await onConfirm(); setOpen(false); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); setText(""); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setText(""); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-white" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display text-red-700">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription></DialogHeader>
        {note && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3.5 space-y-3" data-testid={`${testid}-owner-note`}>
            <p className="text-sm text-amber-800">{note}</p>
            {secondaryAction && (
              <Button variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100" onClick={() => { setOpen(false); secondaryAction.onClick(); }} data-testid={`${testid}-transfer-btn`}>
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
        <div>
          <Label>{t("settings.typeToConfirmPre")}<span className="font-mono font-semibold">{confirmWord}</span>{t("settings.typeToConfirmPost")}</Label>
          <Input value={text} onChange={(e) => setText(e.target.value)} className="mt-1.5" data-testid={`${testid}-input`} autoComplete="off" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testid}-cancel`}>{t("settings.cancel")}</Button>
          <Button className="bg-red-800 hover:bg-red-900 text-white" disabled={text !== confirmWord || busy} onClick={run} data-testid={`${testid}-confirm`}>{busy ? t("settings.working") : t("settings.proceed")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
